import { FormEvent, useEffect, useState } from "react";
import { ArrowLeft, Banknote, FileText, Save } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminInvoice,
  AdminRefund,
  AdminUser,
  createOrderRefund,
  getAdminOrder,
  getCurrentAdmin,
  issueOrderInvoice,
  listOrderInvoices,
  listOrderRefunds,
  logout,
  updateAdminOrder,
  voidOrderInvoice,
} from "./api";
import { Order, OrderStatus, PaymentStatus, invoiceBuyerTypeLabels, orderStatusLabels, paymentMethodLabels, paymentStatusLabels } from "@/services/orders";
import { useConfirmDialog } from "./components/ConfirmDialog";
import { AdminTextTooltip } from "./components/AdminTextTooltip";
import { withImageFallback } from "@/utils/images";

const orderStatusOptions: OrderStatus[] = ["Pending", "Processing", "Shipped", "Completed", "Cancelled"];
// 「部分退款」不放進手動選單，這個狀態只由新增退款時系統自動判斷帶出（見下方「退款紀錄」區塊），
// 避免管理者手動選了這個狀態、卻沒有對應的 Refund 金額紀錄，退款金額對不上實際發生的退款筆數。
const paymentStatusOptions: PaymentStatus[] = ["Unpaid", "Paid", "Refunded"];

/** 訂單狀態往前推進（開始處理／出貨／完成）代表店家已經開始履約，這幾個狀態不應該在還沒收到款項的情況下推進。 */
const PROGRESSED_ORDER_STATUSES: OrderStatus[] = ["Processing", "Shipped", "Completed"];

function isOverdueUnpaid(order: Order): boolean {
  if (order.PaymentStatus !== "Unpaid" || !order.PaymentDueAt) return false;
  if (order.OrderStatus === "Cancelled" || order.OrderStatus === "Completed") return false;
  return new Date(order.PaymentDueAt).getTime() < Date.now();
}

export default function AdminOrderDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [orderStatus, setOrderStatus] = useState<OrderStatus>("Pending");
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>("Unpaid");
  const [adminNote, setAdminNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [refunds, setRefunds] = useState<AdminRefund[]>([]);
  const [refundableAmount, setRefundableAmount] = useState(0);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refundSaving, setRefundSaving] = useState(false);
  const [refundMessage, setRefundMessage] = useState("");
  const [invoices, setInvoices] = useState<AdminInvoice[]>([]);
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [invoiceMessage, setInvoiceMessage] = useState("");
  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load() {
    if (!id) return;
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await getAdminOrder(id);
      setOrder(data.Order);
      setOrderStatus(data.Order.OrderStatus);
      setPaymentStatus(data.Order.PaymentStatus);
      setAdminNote(data.Order.AdminNote || "");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "訂單資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  // 退款紀錄跟訂單狀態放在不同的 API（見後端 RefundApiController 說明：退款是獨立、不可修改的財務紀錄），
  // 這裡失敗就靜默略過（例如這個帳號沒有訂單權限時，主要的 load() 已經會顯示錯誤訊息並導回登入頁，
  // 不需要再跳一次重複的錯誤）。
  async function loadRefunds() {
    if (!id) return;
    try {
      const data = await listOrderRefunds(id);
      setRefunds(data.Refunds);
      setRefundableAmount(data.RefundableAmount);
    } catch {
      // ignore
    }
  }

  // 發票紀錄同樣是獨立的財務台帳（見後端 InvoiceIssuer／Invoice 說明），讀取失敗同樣靜默略過，理由同上方 loadRefunds()。
  async function loadInvoices() {
    if (!id) return;
    try {
      const data = await listOrderInvoices(id);
      setInvoices(data.Invoices);
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    load();
    loadRefunds();
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleIssueInvoice() {
    if (!id) return;
    const confirmed = await confirm({
      title: "確定要開立電子發票？",
      description: "將依訂單目前的買受人資料向電子發票加值中心送出開立請求，開立成功後無法修改內容，如有錯誤需以「作廢」處理後才能重新開立。",
      confirmText: "確定開立",
      cancelText: "取消",
    });
    if (!confirmed) return;

    setInvoiceSaving(true);
    setInvoiceMessage("");
    try {
      await issueOrderInvoice(id);
      await loadInvoices();
      setInvoiceMessage("發票已開立。");
    } catch (error) {
      setInvoiceMessage(error instanceof Error ? error.message : "發票開立失敗，請稍後再試。");
    } finally {
      setInvoiceSaving(false);
    }
  }

  async function handleVoidInvoice(invoice: AdminInvoice) {
    if (!id) return;
    const reason = window.prompt("請輸入作廢原因：");
    if (reason === null) return;
    if (!reason.trim()) {
      setInvoiceMessage("請輸入作廢原因。");
      return;
    }

    const confirmed = await confirm({
      title: "確定要作廢這張發票？",
      description: `發票號碼 ${invoice.InvoiceNumber || "-"} 作廢後無法復原，如需重新開立請於作廢完成後手動再次開立。`,
      confirmText: "確定作廢",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    setInvoiceSaving(true);
    setInvoiceMessage("");
    try {
      await voidOrderInvoice(id, invoice.InvoiceId, reason.trim());
      await loadInvoices();
      setInvoiceMessage("發票已作廢。");
    } catch (error) {
      setInvoiceMessage(error instanceof Error ? error.message : "發票作廢失敗，請稍後再試。");
    } finally {
      setInvoiceSaving(false);
    }
  }

  const invoiceStatusLabels: Record<AdminInvoice["Status"], string> = { Issued: "已開立", Failed: "開立失敗", Voided: "已作廢" };
  const invoiceStatusClass: Record<AdminInvoice["Status"], string> = {
    Issued: "bg-[#e9f4ec] text-[#245438]",
    Failed: "bg-[#fdebea] text-[#8a1f1f]",
    Voided: "bg-[#eeeeee] text-[#59635d]",
  };
  const hasValidInvoice = invoices.some((invoice) => invoice.Status === "Issued");

  async function handleCreateRefund(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!id) return;

    const amount = Number(refundAmount);
    if (!amount || amount <= 0) {
      setRefundMessage("請輸入正確的退款金額。");
      return;
    }

    const confirmed = await confirm({
      title: "確定要新增這筆退款？",
      description: `退款金額 NT$ ${amount.toLocaleString()}${refundReason.trim() ? `，原因：${refundReason.trim()}` : ""}。退款紀錄一經新增無法修改或刪除，請確認金額正確。`,
      confirmText: "確定退款",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    setRefundSaving(true);
    setRefundMessage("");
    try {
      await createOrderRefund(id, { Amount: Math.round(amount), Reason: refundReason.trim() || undefined });
      setRefundAmount("");
      setRefundReason("");
      await Promise.all([load(), loadRefunds()]);
      setMessage("退款紀錄已新增，付款狀態已自動更新。");
    } catch (error) {
      setRefundMessage(error instanceof Error ? error.message : "退款新增失敗，請稍後再試。");
    } finally {
      setRefundSaving(false);
    }
  }

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  async function handleSave() {
    if (!id || !order) return;

    // 訂單狀態往前推進（處理中／已出貨／已完成）卻還沒收到款項，很容易是管理者手滑選錯或忘記先確認收款，
    // 只在「這次真的有變動」時才提醒，避免只是編輯備註、狀態根本沒動也跳出警告造成困擾。
    const statusOrPaymentChanged = orderStatus !== order.OrderStatus || paymentStatus !== order.PaymentStatus;
    if (statusOrPaymentChanged && PROGRESSED_ORDER_STATUSES.includes(orderStatus) && paymentStatus === "Unpaid") {
      const confirmed = await confirm({
        title: "此訂單尚未付款",
        description: `付款狀態目前為「未付款」，仍要將訂單狀態更新為「${orderStatusLabels[orderStatus]}」嗎？建議先確認已收到款項，避免出貨後才發現尚未收款。`,
        confirmText: "仍要更新",
        cancelText: "取消",
        tone: "danger",
      });
      if (!confirmed) return;
    }

    // 訂單已經出貨或完成，代表店家已經履約（甚至商品已經寄出），直接取消可能會漏了處理退貨或退款，需要另外提醒。
    if (statusOrPaymentChanged && orderStatus === "Cancelled" && (order.OrderStatus === "Shipped" || order.OrderStatus === "Completed")) {
      const confirmed = await confirm({
        title: "確定要取消此訂單？",
        description: `此訂單目前狀態為「${orderStatusLabels[order.OrderStatus]}」，取消已出貨或已完成的訂單，需要自行另外處理退貨與退款事宜，系統不會自動退款。確定要取消嗎？`,
        confirmText: "確定取消",
        cancelText: "返回",
        tone: "danger",
      });
      if (!confirmed) return;
    }

    setSaving(true);
    setMessage("");
    try {
      const data = await updateAdminOrder(id, { OrderStatus: orderStatus, PaymentStatus: paymentStatus, AdminNote: adminNote.trim() });
      setOrder(data.Order);
      setMessage("訂單已更新。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "訂單更新失敗。");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout sectionLabel="訂單管理" title={order ? `訂單 ${order.OrderNumber}` : "訂單詳情"} user={currentUser} onLogout={handleLogout}>
      <ConfirmDialog />
      <button className="mb-4 inline-flex h-10 items-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => navigate("/admin/orders")} type="button">
        <ArrowLeft size={18} />
        返回訂單列表
      </button>

      {message && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{message}</div>}

      {loading ? (
        <div className="rounded-md border border-[#dfe6e1] bg-white p-8 text-center text-[#59665f]">載入中...</div>
      ) : !order ? (
        <div className="rounded-md border border-[#dfe6e1] bg-white p-8 text-center text-[#59665f]">查無此訂單。</div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="grid gap-4 lg:col-span-2">
            <section className="grid gap-4 rounded-md border border-[#dfe6e1] bg-white p-4">
              <h2 className="text-base font-bold text-[#315746]">商品明細</h2>
              <div className="divide-y divide-[#edf0ee]">
                {order.Items.map((item, index) => (
                  <div className="flex items-center gap-3 py-3 first:pt-0 last:pb-0" key={`${item.ProductId}-${item.VariantId ?? index}`}>
                    <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md border border-[#edf0ee] bg-[#f6f7f5]">
                      <img className="h-full w-full object-cover" src={withImageFallback(item.ImageUrl)} alt={item.ProductName} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-bold text-[#203d32]">{item.ProductName}</p>
                      {item.VariantLabel && <p className="text-xs text-[#7b877f]">規格：{item.VariantLabel}</p>}
                      <p className="text-xs text-[#7b877f]">SKU：{item.Sku || "-"}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm text-[#59665f]">NT$ {item.UnitPrice.toLocaleString()} × {item.Quantity}</p>
                      <p className="font-bold text-[#203d32]">NT$ {item.LineTotal.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid gap-1.5 border-t border-[#edf0ee] pt-3 text-sm">
                <div className="flex items-center justify-between text-[#59665f]"><span>商品小計</span><span>NT$ {order.SubtotalAmount.toLocaleString()}</span></div>
                {order.DiscountAmount > 0 && (
                  <div className="flex items-center justify-between text-[#59665f]">
                    <span>優惠折扣{order.ShippingCouponCode && <span className="ml-1 text-xs text-[#9a6a1f]">（{order.ShippingCouponCode}）</span>}</span>
                    <span>-NT$ {order.DiscountAmount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-[#59665f]">
                  <span>運費{order.ShippingMethodName ? `（${order.ShippingMethodName}）` : ""}</span>
                  <span>
                    {order.ShippingFee > 0 ? `NT$ ${order.ShippingFee.toLocaleString()}` : "免運費"}
                    {order.ShippingCouponCode && order.DiscountAmount === 0 && <span className="ml-1 text-xs text-[#9a6a1f]">已套用優惠碼 {order.ShippingCouponCode}</span>}
                  </span>
                </div>
                {order.PaymentExtraFee > 0 && (
                  <div className="flex items-center justify-between text-[#59665f]">
                    <span>付款手續費{order.PaymentMethodName ? `（${order.PaymentMethodName}）` : ""}</span>
                    <span>NT$ {order.PaymentExtraFee.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-base font-bold text-[#203d32]"><span>訂單總計</span><span>NT$ {order.TotalAmount.toLocaleString()}</span></div>
              </div>
            </section>

            <section className="grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-4 sm:grid-cols-2">
              <div>
                <h2 className="mb-2 text-base font-bold text-[#315746]">收件資訊</h2>
                <div className="space-y-1 text-sm text-[#59665f]">
                  <p>{order.RecipientName}</p>
                  <p>{order.RecipientPhone}</p>
                  <p>{order.RecipientEmail}</p>
                  {order.PickupLocationName || order.PickupLocationAddress ? (
                    <>
                      <p className="font-bold text-[#315746]">取貨資訊</p>
                      {order.PickupLocationName && <p>{order.PickupLocationName}</p>}
                      {order.PickupLocationAddress && <p>{order.PickupLocationAddress}</p>}
                      {order.PickupLocationPhone && <p>{order.PickupLocationPhone}</p>}
                    </>
                  ) : (
                    <p>{order.ShippingPostalCode ? `${order.ShippingPostalCode} ` : ''}{order.ShippingCity}{order.ShippingDistrict}{order.ShippingAddressLine}</p>
                  )}
                  {order.Notes && <p className="text-[#7b877f]">客人備註：{order.Notes}</p>}
                </div>
              </div>
              <div>
                <h2 className="mb-2 text-base font-bold text-[#315746]">會員資訊</h2>
                {order.MemberId ? (
                  <button
                    className="text-sm font-bold text-[#315746] underline hover:text-[#203d32]"
                    onClick={() => navigate(`/admin/members/${order.MemberId}`)}
                    type="button"
                  >
                    查看會員資料
                  </button>
                ) : (
                  <p className="text-sm text-[#59665f]">訪客訂單</p>
                )}
              </div>
            </section>
          </div>

          <section className="grid gap-4 rounded-md border border-[#dfe6e1] bg-white p-4">
            <h2 className="text-base font-bold text-[#315746]">訂單處理</h2>
            <label className="grid gap-1.5 text-sm font-bold text-[#315746]">
              訂單狀態
              <select className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={orderStatus} onChange={(event) => setOrderStatus(event.target.value as OrderStatus)}>
                {orderStatusOptions.map((value) => <option key={value} value={value}>{orderStatusLabels[value]}</option>)}
              </select>
            </label>
            <label className="grid gap-1.5 text-sm font-bold text-[#315746]">
              付款狀態
              <select className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={paymentStatus} onChange={(event) => setPaymentStatus(event.target.value as PaymentStatus)}>
                {paymentStatusOptions.map((value) => <option key={value} value={value}>{paymentStatusLabels[value]}</option>)}
              </select>
            </label>
            <div className="rounded-md bg-[#f6f7f5] px-3 py-2 text-xs text-[#7b877f]">
              付款方式：{order.PaymentMethodName || paymentMethodLabels[order.PaymentMethod] || order.PaymentMethod}
              {order.PaymentExtraFee > 0 && `（手續費 NT$ ${order.PaymentExtraFee.toLocaleString()}）`}
              {order.PaymentDueAt
                ? "（客人結帳時自選，逾期未付款系統會自動取消訂單，請人工確認收款後更新付款狀態）"
                : "（客人結帳時自選，若為線上金流由系統自動更新付款狀態，其餘請人工確認收款後更新）"}
            </div>
            {order.PaymentDueAt && (
              <div className={`rounded-md px-3 py-2 text-xs font-medium ${isOverdueUnpaid(order) ? "bg-[#fdebea] text-[#8a1f1f]" : "bg-[#f6f7f5] text-[#7b877f]"}`}>
                付款期限：{order.PaymentDueAt}
                {isOverdueUnpaid(order) && "（已逾期，系統排程執行後將自動取消訂單並歸還庫存）"}
              </div>
            )}
            {order.PaymentInstructions && (
              <div className="whitespace-pre-wrap rounded-md bg-[#f6f7f5] px-3 py-2 text-xs leading-5 text-[#7b877f]">
                <span className="font-bold text-[#315746]">客人下單時看到的付款說明：</span>
                {"\n"}
                {order.PaymentInstructions}
              </div>
            )}
            <label className="grid gap-1.5 text-sm font-bold text-[#315746]">
              內部備註
              <textarea className="min-h-[100px] rounded-md border border-[#cfd8d2] px-3 py-2 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="僅後台可見，例如：已電話確認轉帳" value={adminNote} onChange={(event) => setAdminNote(event.target.value)} />
            </label>
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white hover:bg-[#203d32] disabled:opacity-60" disabled={saving} onClick={handleSave} type="button">
              <Save size={18} />
              {saving ? "儲存中..." : "儲存變更"}
            </button>
          </section>

          <section className="grid gap-4 rounded-md border border-[#dfe6e1] bg-white p-4 lg:col-span-3">
            <h2 className="flex items-center gap-2 text-base font-bold text-[#315746]">
              <Banknote size={18} />
              退款紀錄
            </h2>

            {refunds.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[480px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-[#edf0ee] text-xs text-[#7b877f]">
                      <th className="pb-2 font-normal">時間</th>
                      <th className="pb-2 font-normal">金額</th>
                      <th className="pb-2 font-normal">原因</th>
                      <th className="pb-2 font-normal">操作人員</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#edf0ee]">
                    {refunds.map((refund) => (
                      <tr key={refund.RefundId}>
                        <td className="py-2 text-[#59665f]">{refund.CreatedAt || "-"}</td>
                        <td className="py-2 font-bold text-[#8a1f1f]">-NT$ {refund.Amount.toLocaleString()}</td>
                        <td className="max-w-[240px] py-2 text-[#59665f]"><AdminTextTooltip text={refund.Reason} /></td>
                        <td className="py-2 text-[#59665f]">{refund.OperatorName || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-[#7b877f]">尚無退款紀錄。</p>
            )}

            <div className="rounded-md bg-[#f6f7f5] px-3 py-2 text-xs text-[#7b877f]">
              目前可退款餘額：<span className="font-bold text-[#315746]">NT$ {refundableAmount.toLocaleString()}</span>
            </div>

            {refundableAmount > 0 && (
              <form className="grid gap-3 border-t border-[#edf0ee] pt-4 sm:grid-cols-[160px_1fr_auto] sm:items-end" onSubmit={handleCreateRefund}>
                <label className="grid gap-1.5 text-sm font-bold text-[#315746]">
                  退款金額
                  <input
                    className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                    type="number"
                    min={1}
                    max={refundableAmount}
                    placeholder={`最多 NT$ ${refundableAmount.toLocaleString()}`}
                    value={refundAmount}
                    onChange={(event) => setRefundAmount(event.target.value)}
                  />
                </label>
                <label className="grid gap-1.5 text-sm font-bold text-[#315746]">
                  原因（選填）
                  <input
                    className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                    type="text"
                    placeholder="例如：客人取消部分商品"
                    value={refundReason}
                    onChange={(event) => setRefundReason(event.target.value)}
                  />
                </label>
                <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#8a1f1f] px-5 font-bold text-white hover:bg-[#6e1919] disabled:opacity-60" disabled={refundSaving} type="submit">
                  {refundSaving ? "處理中..." : "新增退款"}
                </button>
              </form>
            )}
            {refundMessage && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{refundMessage}</div>}
          </section>

          <section className="grid gap-4 rounded-md border border-[#dfe6e1] bg-white p-4 lg:col-span-3">
            <h2 className="flex items-center gap-2 text-base font-bold text-[#315746]">
              <FileText size={18} />
              電子發票
            </h2>

            <div className="rounded-md bg-[#f6f7f5] px-3 py-2 text-xs text-[#7b877f]">
              買受人類型：{invoiceBuyerTypeLabels[order.InvoiceBuyerType]}
              {order.InvoiceBuyerType === "Company" && order.InvoiceBuyerUBN && `（統編 ${order.InvoiceBuyerUBN}・${order.InvoiceBuyerName || ""}）`}
              {order.InvoiceBuyerType === "Donation" && order.InvoiceLoveCode && `（愛心碼 ${order.InvoiceLoveCode}）`}
            </div>

            {invoices.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-[#edf0ee] text-xs text-[#7b877f]">
                      <th className="pb-2 font-normal">時間</th>
                      <th className="pb-2 font-normal">發票號碼</th>
                      <th className="pb-2 font-normal">金額</th>
                      <th className="pb-2 font-normal">狀態</th>
                      <th className="pb-2 font-normal">說明</th>
                      <th className="pb-2 font-normal">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#edf0ee]">
                    {invoices.map((invoice) => (
                      <tr key={invoice.InvoiceId}>
                        <td className="py-2 text-[#59665f]">{invoice.CreatedAt || "-"}</td>
                        <td className="py-2 font-bold text-[#203d32]">{invoice.InvoiceNumber || "-"}</td>
                        <td className="py-2 text-[#59665f]">NT$ {invoice.Amount.toLocaleString()}</td>
                        <td className="py-2">
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${invoiceStatusClass[invoice.Status]}`}>{invoiceStatusLabels[invoice.Status]}</span>
                        </td>
                        <td className="py-2 text-[#59665f]">{invoice.Status === "Failed" ? invoice.FailReason || "-" : invoice.Status === "Voided" ? invoice.VoidReason || "-" : "-"}</td>
                        <td className="py-2">
                          {invoice.Status === "Issued" && (
                            <button className="inline-flex h-9 items-center justify-center rounded-md border border-[#f2b8b5] bg-white px-3 text-xs font-bold text-[#b3261e] transition hover:bg-[#fdebea] disabled:opacity-60" disabled={invoiceSaving} onClick={() => handleVoidInvoice(invoice)} type="button">
                              作廢
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-[#7b877f]">尚無發票紀錄。</p>
            )}

            {!hasValidInvoice && order.PaymentStatus !== "Unpaid" && (
              <div className="border-t border-[#edf0ee] pt-4">
                <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white hover:bg-[#203d32] disabled:opacity-60" disabled={invoiceSaving} onClick={handleIssueInvoice} type="button">
                  <FileText size={18} />
                  {invoiceSaving ? "處理中..." : "手動開立發票"}
                </button>
              </div>
            )}
            {!hasValidInvoice && order.PaymentStatus === "Unpaid" && (
              <p className="text-xs text-[#9a8a7a]">訂單尚未付款，需先確認收款後才能開立發票。</p>
            )}
            {invoiceMessage && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{invoiceMessage}</div>}
          </section>
        </div>
      )}
    </AdminLayout>
  );
}
